import React, {useState} from 'react';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {useAlert} from 'src/shared/hooks/useAlert';

import {Box} from 'src/design/atoms/Box';
import {useInViewport} from '@mantine/hooks';
import type {NoteModuleEnum} from 'src/api/ProactApiClient';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetNoteListQuery} from 'src/redux/queries/note';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {NoteForm} from './NoteForm';
import {NotesList} from './NotesList';

const maxPageRows = 10;
export interface NotesModuleProps {
  entityId?: number;
  noteType: NoteModuleEnum;
}

export const NotesModule: React.FC<NotesModuleProps> = ({
  entityId,
  noteType,
}) => {
  const {setAlert} = useAlert();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const {ref, inViewport} = useInViewport();

  const {data, isFetching, isError, error} = useGetNoteListQuery(
    {
      start: (currentPage - 1) * maxPageRows,
      length: maxPageRows,
      noteType,
      entityId: entityId!,
    },
    {skip: !entityId || !inViewport}
  );

  React.useEffect(() => {
    if (isError) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [isError, error]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => setCurrentPage(value);

  if (!entityId) {
    return <TableBodyRender hasData={false} />;
  }

  return (
    <>
      <DataHeader titleText="Notes" isFilterActive={false} titleSize="md" />
      <Box>
        <NoteForm
          mode="create"
          entityId={entityId}
          noteType={noteType}
          onSaved={() => {}}
          onCancelled={() => {}}
        />
      </Box>
      <Box ref={ref} mb="xl">
        <NotesList
          entityId={entityId}
          noteType={noteType}
          isLoading={isFetching}
          data={data?.data ?? []}
          currentPage={currentPage}
          recordsPerPage={maxPageRows}
          totalRecords={data?.totalRecords ?? 0}
          onPageChange={handlePageChange}
          onInvalidateState={() => {}}
        />
      </Box>
    </>
  );
};
